import { z } from 'zod';
/**
 * Gift kinds (per ADR-041 / NORTH_STAR §3.2).
 *
 * Legacy (PR -1A baseline, kept for back-compat — no longer surfaced in player UI):
 *   daily_friend_gift / re_invite_gift / event_gift
 *
 * Contextual MVP (PR 21 Slice 1):
 *   heat_nudge        — same-room +3% Heat; cost: 1 spin from sender's current room
 *   fever_spark       — recipient's next Fever +5% contribution; cost: sender 7d Fever win history
 *   streak_shield     — auto-preserves recipient's streak on lapse; cost: 1 day of sender's streak buffer
 *   welcome_drop      — 10K coins + 24h same-room free entry; cost: free, lifetime 1× per (sender, recipient)
 *   spectator_cheer   — remote +1% Heat to recipient's room; cost: 1 of sender's daily 5 cheers
 */
export const GiftKind = z.enum([
    'daily_friend_gift',
    're_invite_gift',
    'event_gift',
    'heat_nudge',
    'fever_spark',
    'streak_shield',
    'welcome_drop',
    'spectator_cheer',
]);
/** Contextual gift kinds — the 5 ADR-041 MVP kinds (excludes legacy). */
export const ContextualGiftKind = z.enum([
    'heat_nudge',
    'fever_spark',
    'streak_shield',
    'welcome_drop',
    'spectator_cheer',
]);
export const GiftContext = z.object({
    room_id: z.string().optional(),
    fever_id: z.string().optional(),
    streak_days_at_send: z.number().int().nonnegative().optional(),
}).catchall(z.unknown());
export const Gift = z.object({
    id: z.number().int(),
    sender_id: z.string(),
    sender_username: z.string(),
    sender_display_name: z.string(),
    sender_avatar_url: z.string().nullable(),
    recipient_id: z.string(),
    amount: z.number().int().nullable(), // PR 21: nullable for non-coin kinds
    kind: GiftKind,
    claimed: z.boolean(),
    expires_at: z.string(),
    created_at: z.string(),
    claimed_at: z.string().nullable(),
    trigger_id: z.string().nullable().optional(),
    context: GiftContext.optional(),
});
export const GiftSendRequest = z.object({
    recipient_user_id: z.string(),
    kind: GiftKind.default('daily_friend_gift'),
    trigger_id: z.string().optional(),
    context: GiftContext.default({}),
});
export const GiftSendResponse = z.object({
    gift_id: z.number().int(),
    sent_at: z.string(),
    next_send_available_at: z.string(),
});
export const GiftInboxRequest = z.object({
    limit: z.number().int().min(1).max(100).default(50),
    offset: z.number().int().min(0).default(0),
    include_claimed: z.boolean().default(false),
});
export const GiftInboxResponse = z.object({
    items: z.array(Gift),
    total_unclaimed: z.number().int(),
    total_unclaimed_amount: z.number().int(),
});
export const GiftClaimRequest = z.object({
    gift_id: z.number().int(),
});
export const GiftClaimResponse = z.object({
    gift_id: z.number().int(),
    amount_claimed: z.number().int(),
    new_balance: z.number().int(),
});
export const GiftClaimAllResponse = z.object({
    claimed_count: z.number().int(),
    total_amount: z.number().int(),
    new_balance: z.number().int(),
});
export const GiftSendStatusRequest = z.object({
    recipient_user_id: z.string(),
});
export const GiftSendStatusResponse = z.object({
    recipient_user_id: z.string(),
    can_send: z.boolean(),
    next_send_available_at: z.string().nullable(),
    daily_amount: z.number().int(),
});
//# sourceMappingURL=gifts.js.map